<template>
  <section class="news-hero">
    <div class="container">
      <div class="news-hero__grid">
        <!-- Слайдер главных новостей -->
        <div class="news-hero__main">
          <div v-if="loading" class="news-hero__card news-hero__skeleton"></div>

          <template v-else-if="slides.length">
            <swiper
              :modules="modules"
              :slides-per-view="1"
              :loop="slides.length > 1"
              :autoplay="{ delay: 6000, disableOnInteraction: false }"
              class="news-hero__slider"
              @swiper="onSwiper"
              @slide-change="onSlideChange"
            >
              <swiper-slide v-for="item in slides" :key="item.id">
                <router-link
                  :to="{ name: 'article_view', params: { id: item.id } }"
                  class="news-hero__card"
                >
                  <img
                    :src="item.image"
                    :alt="item.title"
                    class="news-hero__image"
                  />
                  <div class="news-hero__overlay"></div>

                  <div class="news-hero__content">
                    <div class="news-hero__date">{{ item.created_at }}</div>
                    <h2 class="news-hero__title">{{ item.title }}</h2>
                    <p v-if="item.description" class="news-hero__description">
                      {{ item.description }}
                    </p>
                    <span class="news-hero__more">
                      {{ $t("news.moreDetails") }}
                      <svg-icon name="right" class="w-5 h-5" />
                    </span>
                  </div>
                </router-link>
              </swiper-slide>
            </swiper>

            <div
              v-if="slides.length > 1"
              class="news-hero__pagination news-hero__pagination--desktop"
            >
              <button
                v-for="(item, index) in slides"
                :key="item.id"
                class="news-hero__bullet"
                :class="{ active: index === activeIndex }"
                :aria-label="`${index + 1}`"
                @click="goTo(index)"
              ></button>
            </div>
          </template>

          <div v-if="slides.length > 1" class="news-hero__controls">
            <button
              class="news-hero__nav"
              aria-label="Previous"
              @click="swiper && swiper.slidePrev()"
            >
              <svg-icon name="left" class="w-5 h-5" />
            </button>
            <div class="news-hero__pagination">
              <button
                v-for="(item, index) in slides"
                :key="item.id"
                class="news-hero__bullet"
                :class="{ active: index === activeIndex }"
                :aria-label="`${index + 1}`"
                @click="goTo(index)"
              ></button>
            </div>
            <button
              class="news-hero__nav"
              aria-label="Next"
              @click="swiper && swiper.slideNext()"
            >
              <svg-icon name="right" class="w-5 h-5" />
            </button>
          </div>
        </div>

        <!-- Соңғы жаңалықтар -->
        <div class="news-hero__latest">
          <router-link
            :to="{ name: 'category_view', params: { slug: 'news' } }"
            class="news-hero__latest-title"
          >
            {{ $t("news.latest") }}
          </router-link>

          <div v-if="loading" class="flex flex-col">
            <div v-for="n in 5" :key="n" class="news-hero__item">
              <div class="h-3 w-24 rounded bg-[#F1F1F1] animate-pulse"></div>
              <div
                class="h-4 w-full mt-2 rounded bg-[#F1F1F1] animate-pulse"
              ></div>
            </div>
          </div>

          <router-link
            v-for="item in latest"
            :key="item.id"
            :to="{ name: 'article_view', params: { id: item.id } }"
            class="news-hero__item group"
          >
            <div class="news-hero__item-date">{{ item.created_at }}</div>
            <div class="flex items-start justify-between gap-3">
              <div class="news-hero__item-title">{{ item.title }}</div>
              <svg
                class="news-hero__item-icon"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M7 17L17 7" />
                <path d="M8 7h9v9" />
              </svg>
            </div>
          </router-link>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { Swiper, SwiperSlide } from "swiper/vue";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import SvgIcon from "@/components/SvgIcon.vue";
import { newsService } from "@/services";
import { formatDate } from "@/utils/date";

const SLIDES_COUNT = 4;
const LATEST_COUNT = 5;

const stripHtml = (html) =>
  html
    ? html
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim()
    : "";

export default {
  name: "NewsHero",
  components: {
    Swiper,
    SwiperSlide,
    SvgIcon,
  },
  data() {
    return {
      modules: [Autoplay],
      loading: true,
      slides: [],
      latest: [],
      swiper: null,
      activeIndex: 0,
    };
  },
  created() {
    this.getNews();
  },
  methods: {
    async getNews() {
      try {
        const response = await newsService.getNewsList({
          limit: SLIDES_COUNT + LATEST_COUNT,
        });
        const items = (response.results || []).map((item) => ({
          ...item,
          created_at: formatDate(item.created_at),
          description: stripHtml(item.description),
        }));

        this.slides = items.slice(0, SLIDES_COUNT);
        this.latest = items.slice(SLIDES_COUNT);
      } finally {
        this.loading = false;
      }
    },
    onSwiper(swiper) {
      this.swiper = swiper;
    },
    onSlideChange(swiper) {
      this.activeIndex = swiper.realIndex;
    },
    goTo(index) {
      if (this.swiper) this.swiper.slideToLoop(index);
    },
  },
};
</script>

<style scoped lang="scss">
.news-hero {
  @apply bg-white;

  &__grid {
    @apply flex flex-col gap-6 lg:flex-row lg:gap-8;
  }

  &__main {
    @apply relative w-full min-w-0 lg:w-[66%];
  }

  &__slider {
    @apply rounded-[24px] overflow-hidden;
  }

  &__card {
    @apply relative block w-full h-[360px] sm:h-[420px] lg:h-[480px] rounded-[24px] overflow-hidden bg-[#0d1717];
  }

  &__skeleton {
    @apply bg-[#F1F1F1] animate-pulse;
  }

  &__image {
    @apply absolute inset-0 w-full h-full object-cover;
  }

  &__overlay {
    @apply absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent;
  }

  &__content {
    @apply absolute left-0 right-0 bottom-0 p-4 sm:p-6 lg:p-8 lg:pr-40 text-white;
  }

  &__date {
    @apply text-[13px] leading-[18px] text-white/80 mb-2;
  }

  &__title {
    @apply m-0 text-[20px] leading-[26px] sm:text-[26px] sm:leading-[32px] lg:text-[32px] lg:leading-[40px] font-bold line-clamp-3 lg:line-clamp-2;
  }

  &__description {
    @apply m-0 mt-2 text-[14px] leading-[20px] lg:text-[15px] lg:leading-[22px] text-white/80 line-clamp-2;
  }

  &__more {
    @apply hidden lg:inline-flex items-center gap-1 mt-5 text-[15px] font-semibold text-[#FDC902];
  }

  &__pagination {
    @apply flex items-center gap-1.5;

    &--desktop {
      @apply hidden lg:flex absolute right-8 bottom-9 z-10;

      .news-hero__bullet {
        @apply bg-white/60;

        &.active {
          @apply bg-[#05B242];
        }
      }
    }
  }

  &__bullet {
    @apply h-1 w-4 rounded-full bg-[#DEDEDE] transition-all duration-300 cursor-pointer;

    &.active {
      @apply w-8 bg-[#05B242];
    }
  }

  &__controls {
    @apply flex items-center justify-between mt-4 lg:hidden;
  }

  &__nav {
    @apply w-10 h-10 rounded-full bg-[#F1F1F1] text-[#222] flex items-center justify-center transition-colors duration-300 hover:bg-[#E5E5E5];
  }

  &__latest {
    @apply w-full lg:w-[34%] flex flex-col;
  }

  &__latest-title {
    @apply flex items-center gap-2 pb-2 text-[18px] lg:text-[15px] leading-[22px] font-semibold text-[#222];

    &::before {
      content: "";
      @apply w-1.5 h-1.5 rounded-full bg-[#05B242] flex-shrink-0;
    }
  }

  &__item {
    @apply block py-4 border-b border-solid border-[#EDEDED] last:border-b-0;
  }

  &__item-date {
    @apply text-[13px] leading-[18px] text-[#717171] mb-1;
  }

  &__item-title {
    @apply text-[15px] leading-[21px] font-semibold text-[#222] line-clamp-2 transition-colors duration-200 group-hover:text-[#05B242];
  }

  &__item-icon {
    @apply flex-shrink-0 mt-0.5 text-[#05B242];
  }
}
</style>
